import { Component, OnInit, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { SessionService } from '@services/session.service';

@Component({
  selector: 'app-password-protection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule
  ],
  templateUrl: './password-protection.component.html',
  styleUrls: ['./password-protection.component.scss']
})
export class PasswordProtectionComponent implements OnInit {
  username: string = '';
  password: string = '';
  showPassword: boolean = false;
  errorMessage: string = '';
  attemptCount: number = 0;
  maxAttempts: number = 5;
  isValidating: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<PasswordProtectionComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { errorMessage?: string; isValidating?: boolean; username?: string },
    private sessionService: SessionService
  ) {
    // Disable closing dialog by clicking outside or pressing ESC
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    // Check if this is a validating dialog
    if (this.data?.isValidating) {
      this.isValidating = true;
      this.username = this.data.username || '';
      return;
    }

    // Get stored attempt count from session storage
    const storedAttempts = this.sessionService.getItemFromSession('auth_attempts');
    if (storedAttempts) {
      this.attemptCount = parseInt(storedAttempts, 10);
    }

    // Set initial error message if provided from dialog data
    if (this.data?.errorMessage) {
      this.errorMessage = this.data.errorMessage;
      this.attemptCount++;
      this.sessionService.setItemIntoSession('auth_attempts', this.attemptCount.toString());

      if (this.attemptCount >= this.maxAttempts) {
        this.errorMessage = `Too many failed attempts. Please contact administrator.`;
      }
    }
  }

  // Toggle password visibility
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Submitting credential to parent
  onSubmit(): void {
    // Clear previous error
    this.errorMessage = '';

    // Validate inputs
    if (!this.username || !this.password) {
      this.errorMessage = 'Please enter both username and password';
      return;
    }

    // Emit credentials to be validated by the parent
    this.dialogRef.close({
      username: this.username,
      password: this.password
    });
  }

  // Clear input fields
  onClear(): void {
    // Clear inputs
    this.username = '';
    this.password = '';
    this.errorMessage = '';
  }

  // Set error
  setError(message: string): void {
    this.errorMessage = message;
    this.attemptCount++;
    this.sessionService.setItemIntoSession('auth_attempts', this.attemptCount.toString());

    if (this.attemptCount >= this.maxAttempts) {
      this.errorMessage = `Too many failed attempts. Please contact administrator.`;
    }
  }

  // Calculate attempts
  getRemainingAttempts(): number {
    return Math.max(0, this.maxAttempts - this.attemptCount);
  }

  // Lockdown
  isLocked(): boolean {
    return this.attemptCount >= this.maxAttempts;
  }
}
